import React from 'react';

// services
import {CreditBookApi} from '../../shared/services/ApiService';

// helpers
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

// components
import {CardEmptyState, CardError, PageTemplate} from '../../shared/components';
import {SectionCashbackRequests, SectionSubscriptions} from './sections';
import {LoadingPage} from '../LoadingPage';

// styles
import {IconSubscription} from './SubscriptionsPage-styles';

// types
import {
  CashbackRequest,
  CreditBookApiError,
  Subscription,
} from '../../shared/services/ApiService/types';

/**
 * Fetch the subscriptions
 */
const fetchSubscriptions = async () => {
  const {data} = await CreditBookApi.subscription.getSubscriptions();
  return data;
};

/**
 * Fetch the cashback requests
 */
const fetchCashbackRequests = async () => {
  const {data} = await CreditBookApi.cashbackRequest.getCashbackRequests();
  return data;
};

const SubscriptionsPage = () => {
  const {t} = useTranslation();

  const {
    data: subscriptions = [],
    isLoading: subscriptionsLoading,
    error: subscriptionsError,
    isRefetching: subscriptionsRefetching,
  } = useQuery<Subscription[], CreditBookApiError>({
    queryKey: ['getSubscriptions'],
    queryFn: fetchSubscriptions,
    initialData: [],
  });

  const {
    data: cashbackRequests = [],
    isLoading: cashbackRequestsLoading,
    error: cashbackRequestsError,
    isRefetching: cashbackRequestsRefetching,
  } = useQuery<CashbackRequest[], CreditBookApiError>({
    queryKey: ['getCashbackRequests'],
    queryFn: fetchCashbackRequests,
    initialData: [],
  });

  const displayLoadingPage =
    subscriptionsLoading ||
    subscriptionsRefetching ||
    cashbackRequestsLoading ||
    cashbackRequestsRefetching;

  /**
   * Render the section for cashback requests and request new cashback
   */
  const renderSectionCashbackRequests = () => {
    if (cashbackRequestsError) {
      return (
        <CardError
          title={t('subscription_cashbackRequests')}
          message={cashbackRequestsError?.response?.data?.errorMessage}
        />
      );
    }
    return <SectionCashbackRequests cashbackRequests={cashbackRequests} />;
  };

  /**
   * Render the section for subscriptions
   */
  const renderSectionSubscriptions = () => {
    if (subscriptionsError) {
      return (
        <CardError
          title={t('subscription_subscriptions')}
          message={subscriptionsError?.response?.data?.errorMessage}
        />
      );
    }
    if (subscriptions.length === 0) {
      return (
        <CardEmptyState
          text={t('subscription_emptyState')}
          icon={IconSubscription}
        />
      );
    }
    return <SectionSubscriptions subscriptions={subscriptions} />;
  };

  return (
    <>
      {displayLoadingPage ? (
        <LoadingPage usePortalConfig />
      ) : (
        <PageTemplate
          title={t('subscription_subscriptions')}
          showWarningBanner={true}>
          {renderSectionCashbackRequests()}
          {renderSectionSubscriptions()}
        </PageTemplate>
      )}
    </>
  );
};

export default SubscriptionsPage;
